import React, { useState } from "react";
import { getPracticeId } from "../../config/practice";
import { hasAppointmentId } from "../../utils/visitGuard";
import {
  RETRY_STATUSES,
  athenaCheckinText,
  checkInOnAthena,
  useAthenaCheckinIssue,
} from "./athenaCheckin";
import { clinicTodayKey, startDateKey } from "./visitflow";

const CHIP_CLASSES =
  "athena-checkin-chip inline-flex items-center px-2 py-[1px] rounded-[10px] border border-[#FECACA] bg-[#FEF2F2] text-[#991B1B] text-[11px] font-inter font-medium whitespace-nowrap";

// Red chip on the dashboard card when the visit's Athena check-in needs staff: refused
// (insurance), failed, cancelled in Athena, or blocked (see ./athenaCheckin.js). The reason is in
// the tooltip. Renders nothing otherwise.
//
// canCheckIn (the Visit Confirmation privilege, like the visit status button): on today's card a
// refused / failed check-in is a button - ONE Athena check-in attempt per click, never repeated by
// itself; a success clears the chip, any other answer shows in the tooltip.
const AthenaCheckinChip = ({ row, className = "", canCheckIn = false }) => {
  const issue = useAthenaCheckinIssue(row);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null); // this card's last click: {appointmentid, result, message}
  const lastForIssue = last && issue && last.appointmentid === issue.appointmentid ? last : null;
  // Checked in by the click: hidden at once (the reloaded list says checked_in too).
  if (lastForIssue && lastForIssue.result === "checked_in") return null;
  const day = startDateKey(row?.startdate);
  const canRetry =
    Boolean(canCheckIn) &&
    Boolean(issue) &&
    RETRY_STATUSES.includes(issue.status) &&
    hasAppointmentId(issue.appointmentid) &&
    Boolean(day) &&
    day === clinicTodayKey();
  const text = athenaCheckinText(issue, { canRetry });
  if (!text) return null;
  const title = lastForIssue?.message ? `${lastForIssue.message}\n${text.title}` : text.title;

  if (!canRetry) {
    return (
      <span role="status" data-athena-checkin={issue.status} title={title} className={`${CHIP_CLASSES} ${className}`}>
        {text.label}
      </span>
    );
  }

  const onClick = async (e) => {
    e.stopPropagation(); // the card itself opens the patient on click
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    const appointmentid = issue.appointmentid;
    const out = await checkInOnAthena(getPracticeId(), appointmentid, day);
    setLast({ appointmentid, ...out });
    setBusy(false);
  };

  return (
    <button
      type="button"
      data-athena-checkin={issue.status}
      title={title}
      aria-label={`${text.label} - check in on Athena`}
      aria-busy={busy}
      disabled={busy}
      onClick={onClick}
      className={`${CHIP_CLASSES} cursor-pointer hover:bg-[#FEE2E2] disabled:cursor-wait ${className}`}
    >
      {busy ? "Checking in on Athena..." : text.label}
    </button>
  );
};

export default AthenaCheckinChip;
